import { useState } from "react";

import { SubscriptionCancelReason } from "common/types";
import { FetchError } from "common/fetch/types";
import { Optional } from "common/utils/types";

import Alert from "client/common/components/content/Alert";
import ConfirmationDialog from "client/common/components/content/ConfirmationDialog";
import Select from "client/common/components/inputs/Select";

import { getSubscriptionCancelReasonOptions, isRetentionOfferCancelReason } from "../../utils";
import { SubscriptionCancelRequest } from "../../types";

import styles from "../../styles/SubscriptionCancelReasonStep.module.less";

export type SubscriptionCancelReasonStepProps = {
  onConfirm: (cancelRequest: SubscriptionCancelRequest) => void;
  onDeny: () => void;
  isLoading: boolean;
  /** Whether we are still fetching whether the client has a retention offer to be shown */
  isOfferPreviewLoading: boolean;
  error: Optional<FetchError>;
};

/**
 * Asks why the client is canceling, which is both what we report on and what decides whether we
 * have a retention offer to make them. Rendered as the first step of the cancel dialog.
 */
const SubscriptionCancelReasonStep = ({
  onConfirm,
  onDeny,
  isLoading,
  isOfferPreviewLoading,
  error,
}: SubscriptionCancelReasonStepProps) => {
  const [reason, setReason] = useState<SubscriptionCancelReason | "">("");
  const [reasonOther, setReasonOther] = useState<string>("");
  const isReasonOther = reason === SubscriptionCancelReason.Other;
  const isWaitingForOffer = isOfferPreviewLoading && isRetentionOfferCancelReason(reason);

  const canSubmit = !!reason && (!isReasonOther || !!reasonOther.trim());

  const getSubmitTooltipContent = () => {
    if (!reason) {
      return "Please select a reason to continue";
    }
    if (isReasonOther && !reasonOther.trim()) {
      return "Please specify a reason to continue";
    }
    return undefined;
  };

  const handleConfirm = () => {
    if (!reason || !canSubmit || isWaitingForOffer) {
      return;
    }
    onConfirm({ reason, reasonOther: isReasonOther ? reasonOther : undefined });
  };

  return (
    <ConfirmationDialog
      title="Confirm autoship cancelation"
      description={
        <div className={styles.description}>
          <Alert variant="warning">
            Canceling this autoship will not cancel any orders currently in progress.
          </Alert>
          Before you go, please tell us why you are canceling:
        </div>
      }
      confirmText="Yes, cancel autoship"
      denyText="No, continue shipments"
      onConfirm={handleConfirm}
      onDeny={onDeny}
      isLoading={isLoading || isWaitingForOffer}
      isConfirmDisabled={!canSubmit}
      confirmDisabledTooltipContent={getSubmitTooltipContent()}
      error={error}
    >
      <div className={styles.fields}>
        <Select
          label="Select a cancelation reason"
          value={reason}
          onChange={(r) => setReason(r as SubscriptionCancelReason | "")}
          disabled={isLoading}
        >
          {getSubscriptionCancelReasonOptions().map(({ label, value }) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
        {isReasonOther && (
          <textarea
            className={styles.textarea}
            placeholder="Please specify your reason for canceling..."
            value={reasonOther}
            onChange={(e) => setReasonOther(e.target.value)}
            disabled={isLoading}
            rows={3}
          />
        )}
      </div>
    </ConfirmationDialog>
  );
};

export default SubscriptionCancelReasonStep;
